import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { CategoryDeletionModal } from '../components/CategoryDeletionModal';
import { SubcategoryReassignmentModal } from '../components/SubcategoryReassignmentModal';

interface TransactionCategory {
  id: number;
  name: string;
  color: string;
  is_default: boolean;
  is_income: boolean;
  subcategories: TransactionSubcategory[];
}

interface TransactionSubcategory {
  id: number;
  name: string;
  is_default: boolean;
}

interface RuleCondition {
  id: number;
  field_type: 'merchant' | 'description' | 'amount' | 'account';
  operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'regex' | 'greater_than' | 'less_than';
  value: string;
  condition_order: number;
  logic_operator: 'AND' | 'OR';
}

interface SimpleRulePattern {
  id: number;
  pattern_type: 'merchant' | 'description' | 'amount' | 'account';
  pattern: string;
}

interface CategorizationRule {
  id: number;
  rule_name: string;
  rule_type: 'simple' | 'complex';
  category_id: number | null;
  subcategory_id: number | null;
  confidence: number;
  is_active: boolean;
  conditions?: RuleCondition[];
  simple_patterns?: SimpleRulePattern[];
}

const CategorizationManagement: React.FC = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<TransactionCategory[]>([]);
  const [deletedCategories, setDeletedCategories] = useState<any[]>([]);
  const [rules, setRules] = useState<CategorizationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'expenses' | 'income' | 'rules' | 'deleted'>('expenses');
  
  // Form states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  // const [showRuleForm, setShowRuleForm] = useState(false); // DISABLED
  const [deletionModal, setDeletionModal] = useState<{
    isOpen: boolean;
    categoryId: number | null;
  }>({
    isOpen: false,
    categoryId: null
  });
  const [reassignmentModal, setReassignmentModal] = useState<{
    isOpen: boolean;
    subcategory: { id: number; name: string; categoryName: string } | null;
    transactions: any[];
  }>({
    isOpen: false,
    subcategory: null,
    transactions: []
  });
  const [editingCategory, setEditingCategory] = useState<TransactionCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<{category: TransactionCategory, subcategory: TransactionSubcategory | null}>({category: {} as TransactionCategory, subcategory: null});
  const [editingRule, setEditingRule] = useState<CategorizationRule | null>(null);
  
  // Category form
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#3B82F6',
    type: 'expense' as 'income' | 'expense'
  });
  
  // Subcategory form
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    categoryId: 0
  });
  
  // Rule form - DISABLED: Users should create rules from transactions page
  // const [ruleForm, setRuleForm] = useState({
  //   rule_name: '',
  //   pattern_type: 'merchant' as 'merchant' | 'description' | 'amount' | 'account',
  //   pattern: '',
  //   category_id: 0 as number | null,
  //   subcategory_id: 0 as number | null,
  //   confidence: 0.9
  // });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [categoriesData, rulesData, deletedCategoriesData] = await Promise.all([
        api.transactions.getCategories(),
        api.transactions.getRules(),
        api.transactions.getDeletedCategories()
      ]);
      
      // If no categories exist, populate defaults
      if (categoriesData.length === 0) {
        try {
          await api.transactions.populateDefaults();
          // Reload data after populating defaults
          const [newCategoriesData, newRulesData, newDeletedCategoriesData] = await Promise.all([
            api.transactions.getCategories(),
            api.transactions.getRules(),
            api.transactions.getDeletedCategories()
          ]);
          setCategories(newCategoriesData);
          setRules(newRulesData);
          setDeletedCategories(newDeletedCategoriesData);
        } catch (populateError) {
          console.error('Error populating defaults:', populateError);
          setError('Failed to populate default categories');
        }
      } else {
        setCategories(categoriesData);
        setRules(rulesData);
        setDeletedCategories(deletedCategoriesData);
      }
    } catch (err) {
      console.error('Error loading categorization data:', err);
      setError('Failed to load categorization data');
    } finally {
      setLoading(false);
    }
  };

  const handleResetCategories = async () => {
    if (!window.confirm('This will delete all existing categories and replace them with the new 2-level system. Are you sure?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await api.transactions.resetCategories();
      await loadData();
      
      alert('Categories have been reset to the new 2-level system!');
    } catch (err) {
      console.error('Error resetting categories:', err);
      setError('Failed to reset categories');
    } finally {
      setLoading(false);
    }
  };

  // Separate income and expense categories using the is_income flag from the DB
  const incomeCategories = categories.filter(c => c.is_income === true);
  const expenseCategories = categories.filter(c => c.is_income !== true);

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.transactions.updateCategory(editingCategory.id, {
          name: categoryForm.name,
          color: categoryForm.color,
          is_income: categoryForm.type === 'income',
        });
      } else {
        await api.transactions.createCategory({
          name: categoryForm.name,
          color: categoryForm.color,
          is_income: categoryForm.type === 'income',
        });
      }
      
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', color: '#3B82F6', type: 'expense' });
      loadData();
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category');
    }
  };

  const handleSubcategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSubcategory.subcategory) {
        // Update subcategory
        await api.transactions.updateSubcategory(editingSubcategory.subcategory.id, {
          name: subcategoryForm.name
        });
      } else {
        await api.transactions.createSubcategory(subcategoryForm.categoryId, {
          name: subcategoryForm.name
        });
      }
      
      setShowSubcategoryForm(false);
      setEditingSubcategory({category: {} as TransactionCategory, subcategory: null});
      setSubcategoryForm({ name: '', categoryId: 0 });
      loadData();
    } catch (err) {
      console.error('Error saving subcategory:', err);
      setError('Failed to save subcategory');
    }
  };

  // DISABLED: Old rule submission handler - users should create rules from transactions page
  // const handleRuleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   
  //   // Validate required fields
  //   if (!ruleForm.category_id || ruleForm.category_id === 0) {
  //     alert('Please select a category');
  //     return;
  //   }
  //   
  //   try {
  //     // Convert form values (0 = none) to API values (null = none)
  //     const apiRuleData = {
  //       ...ruleForm,
  //       rule_type: 'simple' as const,
  //       category_id: ruleForm.category_id,
  //       subcategory_id: ruleForm.subcategory_id === 0 ? null : ruleForm.subcategory_id,
  //       simple_patterns: [{
  //         pattern_type: ruleForm.pattern_type,
  //         pattern: ruleForm.pattern
  //       }]
  //     };

  //     if (editingRule) {
  //       await api.transactions.updateRule(editingRule.id, {
  //         ...apiRuleData,
  //         is_active: editingRule.is_active
  //       });
  //     } else {
  //       await api.transactions.createRule(apiRuleData);
  //     }
  //     
  //     // setShowRuleForm(false); // DISABLED
  //     setEditingRule(null);
  //     // Disabled old rule form - users should create rules from transactions page
  //     // setRuleForm({
  //     //   rule_name: '',
  //     //   pattern_type: 'merchant',
  //     //   pattern: '',
  //     //   category_id: 0,
  //     //   subcategory_id: 0,
  //     //   confidence: 0.9
  //     // });
  //     loadData();
  //   } catch (err) {
  //     console.error('Error saving rule:', err);
  //     setError('Failed to save rule');
  //   }
  // };

  const handleAddSubcategory = (category: TransactionCategory) => {
    setEditingSubcategory({category, subcategory: null});
    setSubcategoryForm({ name: '', categoryId: category.id });
    setShowSubcategoryForm(true);
  };

  const handleEditCategory = (category: TransactionCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      color: category.color,
      type: category.is_income ? 'income' : 'expense'
    });
    setShowCategoryForm(true);
  };

  const handleEditRule = (rule: CategorizationRule) => {
    // For now, disable editing since we're using the new rule system
    // Users should create new rules from the transactions page
    alert('To modify rules, please create a new rule from the Transactions page. This will replace the old rule system.');
    return;
    
    // Legacy code for old rule format - commented out
    // setEditingRule(rule);
    // setRuleForm({
    //   rule_name: rule.rule_name,
    //   pattern_type: rule.simple_patterns?.[0]?.pattern_type || 'merchant',
    //   pattern: rule.simple_patterns?.[0]?.pattern || '',
    //   category_id: rule.category_id || 0,
    //   subcategory_id: rule.subcategory_id || 0,
    //   confidence: rule.confidence
    // });
    // setShowRuleForm(true);
  };

  const handleDeleteCategory = (categoryId: number) => {
    setDeletionModal({
      isOpen: true,
      categoryId
    });
  };

  const handleDeletionModalClose = () => {
    setDeletionModal({
      isOpen: false,
      categoryId: null
    });
  };

  const handleCategoryDeleted = () => {
    loadData();
    setDeletionModal({
      isOpen: false,
      categoryId: null
    });
  };

  const handleDeleteSubcategory = async (subcategoryId: number, subcategoryName: string, categoryName: string) => {
    try {
      // Get affected transactions first
      const transactions = await api.transactions.getSubcategoryTransactions(subcategoryId);
      
      if (transactions.length === 0) {
        // No transactions, safe to delete directly
        if (!window.confirm(`Are you sure you want to delete "${categoryName} - ${subcategoryName}"? No transactions are using this subcategory.`)) {
          return;
        }
        
        await api.transactions.deleteSubcategory(subcategoryId);
        loadData();
        setError(null);
      } else {
        // Show reassignment modal
        setReassignmentModal({
          isOpen: true,
          subcategory: { id: subcategoryId, name: subcategoryName, categoryName },
          transactions
        });
      }
    } catch (err) {
      console.error('Error getting subcategory transactions:', err);
      setError('Failed to check subcategory usage');
    }
  };

  const handleReassignmentModalClose = () => {
    setReassignmentModal({
      isOpen: false,
      subcategory: null,
      transactions: []
    });
  };

  const handleBulkReassignAndDelete = async (reassignments: any[], subcategoryId: number) => {
    try {
      await api.transactions.bulkReassignAndDeleteSubcategory(subcategoryId, reassignments);
      loadData();
      setError(null);
    } catch (err) {
      console.error('Error in bulk reassign and delete:', err);
      setError('Failed to reassign transactions and delete subcategory');
      throw err; // Re-throw so modal can handle it
    }
  };

  const handleRestoreCategory = async (categoryId: number) => {
    try {
      await api.transactions.restoreCategory(categoryId);
      loadData();
      setError(null);
    } catch (err) {
      console.error('Error restoring category:', err);
      setError('Failed to restore category');
    }
  };

  const handleRestoreSubcategory = async (subcategoryId: number, categoryId: number) => {
    try {
      await api.transactions.restoreSubcategory(subcategoryId, categoryId);
      loadData();
      setError(null);
    } catch (err) {
      console.error('Error restoring subcategory:', err);
      setError('Failed to restore subcategory');
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('This will restore all default categories and rules. Any custom categories you\'ve created will remain. Continue?')) {
      return;
    }

    try {
      await api.transactions.populateDefaults();
      loadData();
      setError(null);
    } catch (err) {
      console.error('Error restoring defaults:', err);
      setError('Failed to restore default categories. You may already have them.');
    }
  };

  const handleRuleCategoryUpdate = async (ruleId: number, categoryId: number | undefined, subcategoryId: number | undefined) => {
    try {
      // Update the rule via API
      const currentRule = rules.find(r => r.id === ruleId);
      if (!currentRule) return;

      // Validate that categoryId is provided
      if (!categoryId) {
        alert('Please select a category');
        return;
      }

      const updatedRule = {
        rule_name: currentRule.rule_name,
        rule_type: currentRule.rule_type,
        category_id: categoryId,
        subcategory_id: subcategoryId || null,
        confidence: currentRule.confidence,
        is_active: currentRule.is_active,
        conditions: currentRule.conditions,
        simple_patterns: currentRule.simple_patterns
      };

      await api.transactions.updateRule(ruleId, updatedRule);
      
      // Update local state
      setRules(prev => prev.map(rule => 
        rule.id === ruleId 
          ? { ...rule, category_id: categoryId || null, subcategory_id: subcategoryId || null }
          : rule
      ));
    } catch (err) {
      console.error('Error updating rule category:', err);
      setError('Failed to update rule category');
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }
    
    try {
      await api.transactions.deleteRule(ruleId);
      loadData();
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule');
    }
  };

  const handleToggleRule = async (rule: CategorizationRule) => {
    try {
      await api.transactions.toggleRule(rule.id);
      loadData();
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError('Failed to toggle rule');
    }
  };

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading categorization data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="categorization-page">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadData} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Get current tab's categories
  const getCurrentCategories = () => {
    if (activeTab === 'income') {
      return incomeCategories;
    } else if (activeTab === 'expenses') {
      return expenseCategories;
    }
    return [];
  };

  const renderCategoryList = (categoriesList: TransactionCategory[], type: 'income' | 'expense') => (
    <div className="categories-list">
      {categoriesList.map((category) => (
        <div key={category.id} className="category-item">
          <div className="category-main">
            <div 
              className="category-color" 
              style={{ backgroundColor: category.color }}
            ></div>
            <span className="category-name">{category.name}</span>
            {category.is_default && <span className="default-badge">Default</span>}
            
            <div className="category-actions">
              <button onClick={() => handleEditCategory(category)} className="edit-btn">✏️</button>
              <button onClick={() => handleDeleteCategory(category.id)} className="delete-btn">🗑️</button>
            </div>
          </div>
          
          <div className="subcategories-section">
            <div className="subcategories-header">
              <span className="subcategories-label">Subcategories:</span>
              <button 
                onClick={() => handleAddSubcategory(category)}
                className="add-subcategory-btn"
              >
                + Add
              </button>
            </div>
            <div className="subcategories-list">
              {category.subcategories.map((sub) => (
                <span key={sub.id} className="subcategory-tag">
                  {sub.name}
                  <div className="subcategory-actions">
                    <button 
                      className="edit-subcategory"
                      onClick={() => {
                        setEditingSubcategory({category, subcategory: sub});
                        setSubcategoryForm({ name: sub.name, categoryId: category.id });
                        setShowSubcategoryForm(true);
                      }}
                      title={`Edit ${sub.name}`}
                    >
                      ✏️
                    </button>
                    <button 
                      className="remove-subcategory"
                      onClick={() => handleDeleteSubcategory(sub.id, sub.name, category.name)}
                      title={`Delete ${sub.name}${sub.is_default ? ' (default)' : ''}`}
                    >
                      ×
                    </button>
                  </div>
                </span>
              ))}
              {category.subcategories.length === 0 && (
                <span className="no-subcategories">No subcategories</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="categorization-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>Transaction Categories</h1>
            <p>Organize your transactions with custom categories and automated rules</p>
          </div>
          <div className="header-actions">
            <button 
              onClick={handleResetCategories}
              className="btn btn-secondary"
              disabled={loading}
            >
              🔄 Reset to New System
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-container">
        <div className="tabs-nav">
          <button 
            className={`tab-btn ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => setActiveTab('expenses')}
          >
            💸 Expenses
            <span className="tab-count">{expenseCategories.length}</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'income' ? 'active' : ''}`}
            onClick={() => setActiveTab('income')}
          >
            💰 Income
            <span className="tab-count">{incomeCategories.length}</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'rules' ? 'active' : ''}`}
            onClick={() => setActiveTab('rules')}
          >
            🤖 Rules
            <span className="tab-count">{rules.length}</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'deleted' ? 'active' : ''}`}
            onClick={() => setActiveTab('deleted')}
          >
            🗑️ Deleted
            <span className="tab-count">{deletedCategories.length}</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Expenses Tab */}
          {activeTab === 'expenses' && (
            <div className="tab-panel">
              <div className="tab-header">
                <h2>Expense Categories</h2>
                <button 
                  onClick={() => {
                    setCategoryForm({ name: '', color: '#dc2626', type: 'expense' });
                    setShowCategoryForm(true);
                  }}
                  className="btn btn-primary"
                >
                  Add Expense Category
                </button>
              </div>
              {renderCategoryList(expenseCategories, 'expense')}
            </div>
          )}

          {/* Income Tab */}
          {activeTab === 'income' && (
            <div className="tab-panel">
              <div className="tab-header">
                <h2>Income Categories</h2>
                <button 
                  onClick={() => {
                    setCategoryForm({ name: '', color: '#16a34a', type: 'income' });
                    setShowCategoryForm(true);
                  }}
                  className="btn btn-primary"
                >
                  Add Income Category
                </button>
              </div>
              {renderCategoryList(incomeCategories, 'income')}
            </div>
          )}

          {/* Rules Tab */}
          {activeTab === 'rules' && (
            <div className="tab-panel">
              <div className="tab-header">
                <h2>Auto-Categorization Rules</h2>
                <div className="rules-info">
                  <p className="info-text">
                    💡 To create new rules, go to the <strong>Transactions</strong> page and click "⚙️ Create Rule" on any transaction.
                  </p>
                </div>
              </div>
              
              <div className="rules-table-container">
                <table className="rules-table">
                  <thead>
                    <tr>
                      <th className="status-col">Status</th>
                      <th className="name-col">Rule Name</th>
                      <th className="conditions-col">Conditions</th>
                      <th className="category-col">Category</th>
                      <th className="subcategory-col">Subcategory</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule) => {
                      const category = categories.find(c => c.id === rule.category_id);
                      const subcategory = category?.subcategories.find(s => s.id === rule.subcategory_id);
                      
                      return (
                        <tr key={rule.id} className={`rule-row ${rule.is_active ? 'active' : 'inactive'}`}>
                          <td className="status-cell">
                            <button
                              onClick={() => handleToggleRule(rule)}
                              className={`status-toggle ${rule.is_active ? 'active' : 'inactive'}`}
                              title={rule.is_active ? 'Active - Click to disable' : 'Inactive - Click to enable'}
                            >
                              {rule.is_active ? '✓' : '○'}
                            </button>
                          </td>
                          <td className="name-cell">
                            <span className="rule-name">{rule.rule_name}</span>
                          </td>
                          <td className="conditions-cell">
                            <div className="rule-conditions">
                              {rule.rule_type === 'simple' && rule.simple_patterns ? (
                                rule.simple_patterns.map((pattern, index) => (
                                  <span key={index} className="condition-item">
                                    {pattern.pattern_type} contains "{pattern.pattern}"
                                  </span>
                                ))
                              ) : rule.conditions ? (
                                rule.conditions.map((condition, index) => (
                                  <span key={index} className="condition-item">
                                    {index > 0 && <span className="logic-operator">{condition.logic_operator}</span>}
                                    {condition.field_type} {condition.operator} "{condition.value}"
                                  </span>
                                ))
                              ) : (
                                <span className="no-conditions">No conditions</span>
                              )}
                            </div>
                          </td>
                          <td className="category-cell">
                            <select 
                              className="category-select"
                              value={rule.category_id || ''}
                              onChange={(e) => {
                                const newCategoryId = e.target.value ? Number(e.target.value) : undefined;
                                handleRuleCategoryUpdate(rule.id, newCategoryId, undefined);
                              }}
                            >
                              <option value="">Select Category...</option>
                              <optgroup label="💸 Expenses">
                                {expenseCategories.map(cat => (
                                  <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="💰 Income">
                                {incomeCategories.map(cat => (
                                  <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          </td>
                          <td className="subcategory-cell">
                            <select 
                              className="subcategory-select"
                              value={rule.subcategory_id || ''}
                              onChange={(e) => {
                                const newSubcategoryId = e.target.value ? Number(e.target.value) : undefined;
                                handleRuleCategoryUpdate(rule.id, rule.category_id || undefined, newSubcategoryId);
                              }}
                              disabled={!rule.category_id}
                            >
                              <option value="">Select Subcategory...</option>
                              {category?.subcategories.map(sub => (
                                <option key={sub.id} value={sub.id}>
                                  {sub.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button 
                                onClick={() => handleEditRule(rule)} 
                                className="edit-btn"
                                title="Edit rule"
                              >
                                ✏️
                              </button>
                              <button 
                                onClick={() => handleDeleteRule(rule.id)} 
                                className="delete-btn"
                                title="Delete rule"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {rules.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">🤖</div>
                  <h3>No rules yet</h3>
                  <p>Create your first auto-categorization rule to automatically organize transactions</p>
                  <div className="rules-info">
                    <p className="info-text">
                      💡 To create new rules, go to the <strong>Transactions</strong> page and click "⚙️ Create Rule" on any transaction.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Deleted Categories Tab */}
          {activeTab === 'deleted' && (
            <div className="tab-panel">
              <div className="tab-header">
                <h2>Deleted Categories</h2>
                <p className="tab-description">Categories that have been deleted can be restored here.</p>
              </div>
              
              {deletedCategories.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">✨</div>
                  <h3>No deleted categories</h3>
                  <p>All your categories are active. Deleted categories will appear here for restoration.</p>
                </div>
              ) : (
                <div className="deleted-categories-list">
                  {deletedCategories.map((deletedCategory) => (
                    <div key={deletedCategory.id} className="deleted-category-item">
                      <div className="deleted-category-main">
                        <div className="category-info">
                          <div 
                            className="category-color-dot" 
                            style={{ backgroundColor: deletedCategory.color || '#ccc' }}
                          ></div>
                          <div className="category-details">
                            <span className="category-name">{deletedCategory.name}</span>
                            {deletedCategory.is_default && (
                              <span className="default-badge">Default</span>
                            )}
                            <div className="deletion-info">
                              Deleted: {new Date(deletedCategory.deleted_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <button 
                          className="btn btn-success btn-sm"
                          onClick={() => handleRestoreCategory(deletedCategory.id)}
                        >
                          🔄 Restore
                        </button>
                      </div>
                      
                      {/* Show orphaned subcategories if any */}
                      {deletedCategory.deleted_subcategories && deletedCategory.deleted_subcategories.length > 0 && (
                        <div className="orphaned-subcategories">
                          <h5>Orphaned Subcategories:</h5>
                          <div className="orphaned-subcategories-list">
                            {deletedCategory.deleted_subcategories.map((sub: any) => (
                              <div key={sub.id} className="orphaned-subcategory">
                                <span className="subcategory-name">{sub.name}</span>
                                <div className="subcategory-actions">
                                  <select 
                                    className="restore-to-category"
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleRestoreSubcategory(sub.id, Number(e.target.value));
                                      }
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">Choose category to restore to...</option>
                                    {categories.map(cat => (
                                      <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Restore Defaults Section */}
          <div className="restore-defaults-section">
            <h4>System Options</h4>
            <p className="restore-description">
              If you've deleted default categories and want them back, you can restore the original categories and rules.
            </p>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={handleRestoreDefaults}
              disabled={loading}
            >
              Restore Default Categories & Rules
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      
      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : `Add ${categoryForm.type === 'income' ? 'Income' : 'Expense'} Category`}</h3>
              <button 
                onClick={() => {
                  setShowCategoryForm(false);
                  setEditingCategory(null);
                  setCategoryForm({ name: '', color: '#3B82F6', type: 'expense' });
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCategorySubmit} className="modal-body">
              <div className="form-group">
                <label className="form-label">Type</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${categoryForm.type === 'income' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setCategoryForm({ ...categoryForm, type: 'income' })}
                  >
                    Income
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${categoryForm.type === 'expense' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setCategoryForm({ ...categoryForm, type: 'expense' })}
                  >
                    Expense
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Category Name</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="form-input"
                  placeholder={`e.g., ${categoryForm.type === 'income' ? 'Freelance Work' : 'Groceries'}`}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="color-text"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCategoryForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Form Modal */}
      {showSubcategoryForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Subcategory to "{editingSubcategory.category.name}"</h3>
              <button 
                onClick={() => {
                  setShowSubcategoryForm(false);
                  setEditingSubcategory({category: {} as TransactionCategory, subcategory: null});
                  setSubcategoryForm({ name: '', categoryId: 0 });
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubcategorySubmit} className="modal-body">
              <div className="form-group">
                <label className="form-label">Subcategory Name</label>
                <input
                  type="text"
                  value={subcategoryForm.name}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Fast Food, Coffee Shops"
                  required
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowSubcategoryForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rule Form Modal - Disabled, using new rule creator from transactions page */}
      {/* DISABLED: Rule form modal - users should create rules from transactions page
      {false && showRuleForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingRule ? 'Edit Rule' : 'Add Auto-Categorization Rule'}</h3>
              <button 
                onClick={() => {
                  // setShowRuleForm(false); // DISABLED
                  setEditingRule(null);
                  // Disabled old rule form - users should create rules from transactions page
                  // setRuleForm({
                  //   rule_name: '',
                  //   pattern_type: 'merchant',
                  //   pattern: '',
                  //   category_id: 0,
                  //   subcategory_id: 0,
                  //   confidence: 0.9
                  // });
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleRuleSubmit} className="modal-body">
              <div className="form-group">
                <label className="form-label">Rule Name</label>
                <input
                  type="text"
                  value={ruleForm.rule_name}
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_name: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Starbucks Coffee"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Pattern Type</label>
                  <select
                    value={ruleForm.pattern_type}
                    onChange={(e) => setRuleForm({ ...ruleForm, pattern_type: e.target.value as any })}
                    className="form-select"
                  >
                    <option value="merchant">Merchant Name</option>
                    <option value="description">Description</option>
                    <option value="amount">Amount</option>
                    <option value="account">Account</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Pattern</label>
                  <input
                    type="text"
                    value={ruleForm.pattern}
                    onChange={(e) => setRuleForm({ ...ruleForm, pattern: e.target.value })}
                    className="form-input"
                    placeholder={
                      ruleForm.pattern_type === 'merchant' ? 'STARBUCKS' :
                      ruleForm.pattern_type === 'description' ? 'POS PURCHASE' :
                      ruleForm.pattern_type === 'amount' ? '>100' : 'Checking'
                    }
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    value={ruleForm.category_id || 0}
                    onChange={(e) => setRuleForm({ ...ruleForm, category_id: Number(e.target.value), subcategory_id: 0 })}
                    className="form-select"
                    required
                  >
                    <option value={0}>Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {(ruleForm.category_id || 0) > 0 && (
                  <div className="form-group">
                    <label className="form-label">Subcategory</label>
                    <select
                      value={ruleForm.subcategory_id || 0}
                      onChange={(e) => setRuleForm({ ...ruleForm, subcategory_id: Number(e.target.value) })}
                      className="form-select"
                    >
                      <option value={0}>Select Subcategory (Optional)</option>
                      {categories
                        .find(c => c.id === ruleForm.category_id)
                        ?.subcategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowRuleForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRule ? 'Update' : 'Create'} Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      */}

      {/* Category Deletion Modal */}
      {deletionModal.categoryId && (
        <CategoryDeletionModal
          isOpen={deletionModal.isOpen}
          onClose={handleDeletionModalClose}
          categoryId={deletionModal.categoryId}
          onDeleted={handleCategoryDeleted}
        />
      )}

      {/* Subcategory Reassignment Modal */}
      <SubcategoryReassignmentModal
        isOpen={reassignmentModal.isOpen}
        onClose={handleReassignmentModalClose}
        subcategory={reassignmentModal.subcategory}
        transactions={reassignmentModal.transactions}
        categories={categories}
        onSave={handleBulkReassignAndDelete}
      />
    </div>
  );
};

export default CategorizationManagement;